import React, { useEffect, useState } from "react";
import { CSVLink } from "react-csv";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import API from "../../../services/api";
import { Toast } from "../../../shared/Toast";
import { toLakhs, BrandsTypes } from "../../../utils/constants";
import Select from "react-select";
import SheetFourLoader from "./SheetFourLoader";
import useCategories from "../useCategories";

const computeMetrics = ({ location, actualRevenue, plannedRevenue, lastYearRevenue, year, isTotal, locations }) => {
  const calcPercent = (r, p) => (p > 0 ? `${((r / p) * 100)?.toFixed(1)}%` : "-");

  const locs = isTotal ? locations : [location];
  const monthPlanned = new Array(12).fill(0);
  const monthRevenue = new Array(12).fill(0);

  locs.forEach((loc) => {
    actualRevenue?.forEach(({ location: l, month, amount }) => {
      if (l === loc) {
        const monthIdx = parseInt(month?.split("-")[1]) - 1;
        monthRevenue[monthIdx] += parseFloat(amount ?? 0);
      }
    });
    plannedRevenue?.forEach(({ location: l, month, amount }) => {
      if (l === loc) {
        const monthIdx = parseInt(month) - 1;
        monthPlanned[monthIdx] += parseFloat(amount ?? 0);
      }
    });
  });

  const q1P = monthPlanned[0] + monthPlanned[1] + monthPlanned[2];
  const q1R = monthRevenue[0] + monthRevenue[1] + monthRevenue[2];
  const q2P = monthPlanned[3] + monthPlanned[4] + monthPlanned[5];
  const q2R = monthRevenue[3] + monthRevenue[4] + monthRevenue[5];
  const q3P = monthPlanned[6] + monthPlanned[7] + monthPlanned[8];
  const q3R = monthRevenue[6] + monthRevenue[7] + monthRevenue[8];
  const q4P = monthPlanned[9] + monthPlanned[10] + monthPlanned[11];
  const q4R = monthRevenue[9] + monthRevenue[10] + monthRevenue[11];
  const h1P = q1P + q2P;
  const h1R = q1R + q2R;
  const h2P = q3P + q4P;
  const h2R = q3R + q4R;
  const cyP = h1P + h2P;
  const cyR = h1R + h2R;
  const lyR = isTotal ? lastYearRevenue?.reduce((sum, { amount }) => sum + parseFloat(amount ?? 0), 0) ?? 0 : lastYearRevenue?.find(({ location: l }) => l === location)?.amount ?? 0;

  return {
    monthPlanned,
    monthRevenue,
    q1P,
    q1R,
    q2P,
    q2R,
    q3P,
    q3R,
    q4P,
    q4R,
    h1P,
    h1R,
    h2P,
    h2R,
    cyP,
    cyR,
    lyR,
    monthPercent: monthPlanned?.map((p, i) => calcPercent(monthRevenue[i], p)),
    q1Percent: calcPercent(q1R, q1P),
    q2Percent: calcPercent(q2R, q2P),
    q3Percent: calcPercent(q3R, q3P),
    q4Percent: calcPercent(q4R, q4P),
    h1Percent: calcPercent(h1R, h1P),
    h2Percent: calcPercent(h2R, h2P),
    cyPercent: calcPercent(cyR, cyP),
  };
};

const TableSection = ({ location, actualRevenue, plannedRevenue, lastYearRevenue, year, isTotal, locations }) => {
  const metrics = computeMetrics({ location, actualRevenue, plannedRevenue, lastYearRevenue, year, isTotal, locations });

  const getCellValue = (rowIdx, valueP, valueR, valuePercent) => (rowIdx === 0 ? toLakhs(valueP) : rowIdx === 1 ? toLakhs(valueR) : valuePercent);

  return (
    <div className="flex flex-col md:flex-row gap-3 w-full">
      <table className="w-full table-fixed border-collapse border border-gray-600 text-xs md:text-sm rounded-lg shadow-md">
        <thead>
          <tr>
            {["Location", "Metric", "Jan", "Feb", "Mar", "Q1", "Apr", "May", "June", "Q2", "Q1+Q2"]?.map((h, i) => (
              <th key={h} className={`w-24 border border-gray-600 text-center p-1 ${[5, 9, 10].includes(i) ? "bg-yellow-200" : ""}`}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {["Target", "Achieved", "%"]?.map((metric, rowIdx) => (
            <tr key={metric}>
              {rowIdx === 0 && (
                <td rowSpan="3" className="border border-gray-600 text-center p-1 font-bold">
                  {location}
                </td>
              )}
              <td className="border border-gray-600 text-center p-1">{metric}</td>
              {[0, 1, 2, undefined, 3, 4, 5, undefined, undefined]?.map((m, i) => {
                if (i === 3)
                  return (
                    <td key="q1" className="border border-gray-600 text-center p-1 bg-yellow-200" title={rowIdx === 0 ? metrics.q1P : rowIdx === 1 ? metrics.q1R : ""}>
                      {getCellValue(rowIdx, metrics.q1P, metrics.q1R, metrics.q1Percent)}
                    </td>
                  );
                if (i === 7)
                  return (
                    <td key="q2" className="border border-gray-600 text-center p-1 bg-yellow-200" title={rowIdx === 0 ? metrics.q2P : rowIdx === 1 ? metrics.q2R : ""}>
                      {getCellValue(rowIdx, metrics.q2P, metrics.q2R, metrics.q2Percent)}
                    </td>
                  );
                if (i === 8)
                  return (
                    <td key="h1" className="border border-gray-600 text-center p-1 bg-yellow-200" title={rowIdx === 0 ? metrics.h1P : rowIdx === 1 ? metrics.h1R : ""}>
                      {getCellValue(rowIdx, metrics.h1P, metrics.h1R, metrics.h1Percent)}
                    </td>
                  );
                return (
                  <td key={i} className="border border-gray-600 text-center p-1" title={rowIdx === 0 ? metrics.monthPlanned[m] : rowIdx === 1 ? metrics.monthRevenue[m] : ""}>
                    {getCellValue(rowIdx, metrics.monthPlanned[m], metrics.monthRevenue[m], metrics.monthPercent[m])}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <table className="w-full table-fixed border-collapse border border-gray-600 text-xs md:text-sm rounded-lg shadow-md">
        <thead>
          <tr>
            {["Location", "Metric", "July", "Aug", "Sept", "Q3", "Oct", "Nov", "Dec", "Q4", "Q3+Q4"]?.map((h, i) => (
              <th key={h} className={`w-24 border border-gray-600 text-center p-1 ${[5, 9, 10].includes(i) ? "bg-yellow-200" : ""}`}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {["Target", "Achieved", "%"]?.map((metric, rowIdx) => (
            <tr key={metric}>
              {rowIdx === 0 && (
                <td rowSpan="3" className="border border-gray-600 text-center p-1 font-bold">
                  {location}
                </td>
              )}
              <td className="border border-gray-600 text-center p-1">{metric}</td>
              {[6, 7, 8, undefined, 9, 10, 11, undefined, undefined]?.map((m, i) => {
                if (i === 3)
                  return (
                    <td key="q3" className="border border-gray-600 text-center p-1 bg-yellow-200" title={rowIdx === 0 ? metrics.q3P : rowIdx === 1 ? metrics.q3R : ""}>
                      {getCellValue(rowIdx, metrics.q3P, metrics.q3R, metrics.q3Percent)}
                    </td>
                  );
                if (i === 7)
                  return (
                    <td key="q4" className="border border-gray-600 text-center p-1 bg-yellow-200" title={rowIdx === 0 ? metrics.q4P : rowIdx === 1 ? metrics.q4R : ""}>
                      {getCellValue(rowIdx, metrics.q4P, metrics.q4R, metrics.q4Percent)}
                    </td>
                  );
                if (i === 8)
                  return (
                    <td key="h2" className="border border-gray-600 text-center p-1 bg-yellow-200" title={rowIdx === 0 ? metrics.h2P : rowIdx === 1 ? metrics.h2R : ""}>
                      {getCellValue(rowIdx, metrics.h2P, metrics.h2R, metrics.h2Percent)}
                    </td>
                  );
                return (
                  <td key={i} className="border border-gray-600 text-center p-1" title={rowIdx === 0 ? metrics.monthPlanned[m] : rowIdx === 1 ? metrics.monthRevenue[m] : ""}>
                    {getCellValue(rowIdx, metrics.monthPlanned[m], metrics.monthRevenue[m], metrics.monthPercent[m])}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <table className="w-24 table-fixed border-collapse border border-gray-600 text-xs md:text-sm rounded-lg shadow-md">
        <thead>
          <tr>
            {["Location", "Metric", `CY ${year}`, "Last Year"]?.map((h, i) => (
              <th key={h} className={`w-24 border border-gray-600 text-center p-1 ${i === 2 ? "bg-green-400" : i === 3 ? "bg-red-400" : ""}`}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {["Target", "Achieved", "%"]?.map((metric, rowIdx) => (
            <tr key={metric}>
              {rowIdx === 0 && (
                <td rowSpan="3" className="border border-gray-600 text-center p-1 font-bold">
                  {location}
                </td>
              )}
              <td className="border border-gray-600 text-center p-1">{metric}</td>
              <td className="border border-gray-600 text-center p-1 bg-green-400" title={rowIdx === 0 ? metrics.cyP : rowIdx === 1 ? metrics.cyR : ""}>
                {getCellValue(rowIdx, metrics.cyP, metrics.cyR, metrics.cyPercent)}
              </td>
              <td className="border border-gray-600 text-center p-1 bg-red-400" title={rowIdx === 1 ? metrics.lyR : ""}>
                {rowIdx === 1 ? toLakhs(metrics.lyR) : ""}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SheetFourPreview = () => {
  const [year, setYear] = useState(2025);
  const [data, setData] = useState({ actualRevenue: [], plannedRevenue: [], lastYearRevenue: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [locations, setLocations] = useState([]);
  const [csvData, setCsvData] = useState([]);
  const [selectedSalesPerson, setSelectedSalesPerson] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [selectedBrandType, setSelectedBrandType] = useState(2);
  const {CategorySelect, selectedCategories} = useCategories()

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const result = await API({ url: "/phionex_reports/brandLocations", method: "POST" });
        if (result.status === "SUCCESS") {
          setLocations(result.data);
        } else {
          Toast("error", "Failed To Get Locations");
        }
      } catch {
        Toast("error", "An error occurred while fetching locations");
      }
    };
    fetchLocations();
  }, []);

  useEffect(() => {
    // if (!selectedBrandType) {
    //   setSalesPersons([]);
    //   setSelectedSalesPerson([]);
    //   return;
    // }
    const getSalesPersons = async () => {
      const result = await API({
        url: "/phionex_reports/getSalesPersons",
        method: "POST",
        data: { selectedBrandType },
      });
      if (result.status === "SUCCESS") setSalesPersons(result.data);
      else Toast("error", result.message);
    };
    getSalesPersons();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const result = await API({
          url: "/phionex_reports/getSheet4Data",
          method: "POST",
          data: { year, brand_type: selectedBrandType, sales_person: selectedSalesPerson,selectedCategories },
        });
        if (result.status === "SUCCESS") {
          setData({
            actualRevenue: result.data.actualRevenue ?? [],
            plannedRevenue: result.data.plannedRevenue ?? [],
            lastYearRevenue: result.data.LastYear ?? [],
          });
        } else {
          Toast("error", result.message ?? "Failed To Fetch Data");
        }
      } catch {
        Toast("error", "An error occurred while fetching data");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [year, selectedBrandType, selectedSalesPerson,selectedCategories]);

  useEffect(() => {
    if (isLoading) return;

    const allLocations = [...new Set([...data.actualRevenue, ...data.plannedRevenue, ...data.lastYearRevenue]?.map(({ location }) => location))];
    const filteredLocations = allLocations?.filter((loc) => !selectedLocation || loc?.toLowerCase() === selectedLocation?.toLowerCase());

    const addToCsv = (csv, loc, met, year) => {
      csv.push({
        Location: loc,
        Metric: "Target",
        Jan: met.monthPlanned[0],
        Feb: met.monthPlanned[1],
        Mar: met.monthPlanned[2],
        Q1: met.q1P,
        Apr: met.monthPlanned[3],
        May: met.monthPlanned[4],
        Jun: met.monthPlanned[5],
        Q2: met.q2P,
        "Q1+Q2": met.h1P,
        Jul: met.monthPlanned[6],
        Aug: met.monthPlanned[7],
        Sep: met.monthPlanned[8],
        Q3: met.q3P,
        Oct: met.monthPlanned[9],
        Nov: met.monthPlanned[10],
        Dec: met.monthPlanned[11],
        Q4: met.q4P,
        "Q3+Q4": met.h2P,
        [`CY ${year}`]: met.cyP,
        last_year_revenue: "",
      });
      csv.push({
        Location: "",
        Metric: "Achieved",
        Jan: met.monthRevenue[0],
        Feb: met.monthRevenue[1],
        Mar: met.monthRevenue[2],
        Q1: met.q1R,
        Apr: met.monthRevenue[3],
        May: met.monthRevenue[4],
        Jun: met.monthRevenue[5],
        Q2: met.q2R,
        "Q1+Q2": met.h1R,
        Jul: met.monthRevenue[6],
        Aug: met.monthRevenue[7],
        Sep: met.monthRevenue[8],
        Q3: met.q3R,
        Oct: met.monthRevenue[9],
        Nov: met.monthRevenue[10],
        Dec: met.monthRevenue[11],
        Q4: met.q4R,
        "Q3+Q4": met.h2R,
        [`CY ${year}`]: met.cyR,
        last_year_revenue: met.lyR,
      });
      csv.push({
        Location: "",
        Metric: "%",
        Jan: met.monthPercent[0],
        Feb: met.monthPercent[1],
        Mar: met.monthPercent[2],
        Q1: met.q1Percent,
        Apr: met.monthPercent[3],
        May: met.monthPercent[4],
        Jun: met.monthPercent[5],
        Q2: met.q2Percent,
        "Q1+Q2": met.h1Percent,
        Jul: met.monthPercent[6],
        Aug: met.monthPercent[7],
        Sep: met.monthPercent[8],
        Q3: met.q3Percent,
        Oct: met.monthPercent[9],
        Nov: met.monthPercent[10],
        Dec: met.monthPercent[11],
        Q4: met.q4Percent,
        "Q3+Q4": met.h2Percent,
        [`CY ${year}`]: met.cyPercent,
        last_year_revenue: "",
      });
    };

    const newCsvData = [];
    if (!selectedLocation) {
      const totalMetrics = computeMetrics({
        location: "Total",
        actualRevenue: data.actualRevenue,
        plannedRevenue: data.plannedRevenue,
        lastYearRevenue: data.lastYearRevenue,
        year,
        isTotal: true,
        locations: allLocations,
      });
      addToCsv(newCsvData, "Total", totalMetrics, year);
    }
    filteredLocations.forEach((loc) => {
      const locMetrics = computeMetrics({
        location: loc,
        actualRevenue: data.actualRevenue,
        plannedRevenue: data.plannedRevenue,
        lastYearRevenue: data.lastYearRevenue,
        year,
        isTotal: false,
        locations: allLocations,
      });
      addToCsv(newCsvData, loc, locMetrics, year);
    });
    setCsvData(newCsvData);
  }, [data, year, selectedLocation, isLoading]);

  const allLocations = [...new Set([...data.actualRevenue, ...data.plannedRevenue, ...data.lastYearRevenue]?.map(({ location }) => location))];
  const filteredLocations = allLocations?.filter((loc) => !selectedLocation || loc?.toLowerCase() === selectedLocation?.toLowerCase());

  return (
    <div className="p-4 max-w-full">
      <div className="flex flex-wrap gap-6 items-end rounded px-4 py-2 bg-white">
        <div className="flex items-center mb-4 sm:mb-0">
          <label className="mr-2 font-semibold text-sm md:text-base">Select Year:</label>
          <DatePicker
            selected={new Date(year, 0, 1)}
            onChange={(date) => setYear(date.getFullYear())}
            showYearPicker
            dateFormat="yyyy"
            className="border border-gray-300 rounded text-center px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
          />
        </div>
        <div className="flex flex-col mb-4 min-w-[180px]">
          <Select
            options={locations}
            value={locations?.find((l) => l.value === selectedLocation) || null}
            onChange={(item) => setSelectedLocation(item?.value || null)}
            placeholder="Select Location"
            isClearable
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
        <div className="flex flex-col mb-4 min-w-[180px]">
          <Select
            options={BrandsTypes}
            value={BrandsTypes?.find((b) => +b.value === +selectedBrandType) ?? null}
            onChange={(item) => setSelectedBrandType(item?.value ?? null)}
            placeholder="Select Brand Type"
            isClearable
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
        <div className={`flex flex-col mb-4 min-w-[220px] ${selectedBrandType ? "" : "opacity-50 pointer-events-none"}`}>
          <Select
            options={salesPersons}
            value={salesPersons?.filter((p) => selectedSalesPerson.includes(+p.value))}
            onChange={(items) => setSelectedSalesPerson(items ? items?.map((i) => i.value) : [])}
            isMulti
            isClearable
            placeholder="Select Sales Person(s)"
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            closeMenuOnSelect={false}
          />
        </div>
              <CategorySelect />

        <CSVLink data={csvData} filename={`way2news_report_${year}.csv`} className="inline-block px-4 py-2 mb-4 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm transition-colors">
          Download CSV
        </CSVLink>
      </div>
      {isLoading ? (
        <SheetFourLoader />
      ) : (
        <div className="space-y-6 min-w-full">
          {!selectedLocation && <TableSection location="Total" key="Total" actualRevenue={data.actualRevenue} plannedRevenue={data.plannedRevenue} lastYearRevenue={data.lastYearRevenue} year={year} isTotal={true} locations={allLocations} />}
          {filteredLocations?.map((location) => (
            <TableSection key={location} location={location} actualRevenue={data.actualRevenue} plannedRevenue={data.plannedRevenue} lastYearRevenue={data.lastYearRevenue} year={year} isTotal={false} locations={allLocations} />
          ))}
        </div>
      )}
    </div>
  );
};

export default SheetFourPreview;